import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import PageRenderer from '../../components/common/PageRenderer';

function AnnouncementsSection() {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get('/announcements/').then(res => {
      setAnnouncements(res.data.results || res.data || []);
    }).catch(() => {});
  }, []);

  if (announcements.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] section-heading">
              Latest News & Announcements
            </h2>
          </div>
          <Link to="/about" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[#0B5D1E] hover:text-[#1A7A35] link-underline transition">
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {announcements.slice(0, 3).map((a) => (
            <article key={a.id} className="card-hover bg-[#F8FAFB] rounded-xl overflow-hidden border border-gray-100">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
                    a.priority === 'urgent' ? 'badge-urgent' :
                    a.priority === 'high' ? 'badge-high' : 'badge-normal'
                  }`}>
                    {a.priority}
                  </span>
                  <time className="text-xs text-gray-400 font-medium">
                    {new Date(a.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </time>
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-3 leading-snug line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {a.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{a.content}</p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-[#0B5D1E] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    Read more
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryGrid() {
  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {['/Surveyor001.jpeg', '/Surveyor002.jpeg', '/Surveyor003.jpeg', '/Surveyor004.jpeg'].map((src, i) => (
          <div key={i} className="relative group overflow-hidden rounded-xl aspect-[4/3]">
            <img src={src} alt={`Surveyor at work ${i + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DefaultContent() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B5D1E]" style={{ backgroundImage: 'linear-gradient(135deg, #0B5D1E 0%, #094A18 100%)' }}>
        <div className="relative" style={{ paddingTop: 100, paddingBottom: 100, zIndex: 1 }}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 700, color: '#fff', marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>
              Nigerian Institution of Surveyors
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto" style={{ marginBottom: 32 }}>
              FCT Branch — Promoting excellence, integrity, and professionalism in the surveying profession
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/about" className="inline-flex items-center gap-2 bg-white text-[#0B5D1E] px-6 py-3 rounded-lg text-sm font-bold hover:bg-gray-100 transition shadow-md">
                Learn More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
              <Link to="/find-surveyor" className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-white/10 transition">
                Find a Surveyor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display',serif" }}>What We Offer</h2>
          <p className="text-gray-500 max-w-2xl mx-auto mb-12">
            Dedicated to supporting surveyors and advancing the profession in the Federal Capital Territory
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🎓', title: 'Professional Development', desc: 'Workshops, seminars, and CPD programs to keep members at the forefront of surveying practice.' },
              { icon: '🤝', title: 'Networking', desc: 'Connect with fellow surveyors, mentors, and industry leaders across the FCT and Nigeria.' },
              { icon: '📋', title: 'Regulation & Standards', desc: 'Upholding ethical standards and ensuring quality service delivery in all surveying activities.' },
              { icon: '🏛️', title: 'Advocacy', desc: 'Representing members\' interests at government and institutional levels for a better profession.' },
            ].map((item) => (
              <div key={item.title} className="bg-[#F8FAFB] rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-[#0B5D1E] mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements */}
      <AnnouncementsSection />

      {/* Gallery */}
      <section className="py-16 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display',serif" }}>Gallery</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Highlights from our events, workshops, and activities</p>
          </div>
          <GalleryGrid />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0B5D1E]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display',serif" }}>Join the NIS FCT Branch Community</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Whether you're a licensed surveyor, graduate, or student, there's a place for you in our community.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#C8A951] text-white px-8 py-3.5 rounded-lg text-sm font-bold hover:bg-[#B8993F] transition shadow-lg">
            Get in Touch
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </div>
      </section>
    </>
  );
}

export default function Home() {
  return (
    <PageRenderer
      pageKey="home"
      insertAfter={{
        'Features Grid — What We Offer': () => <AnnouncementsSection />,
        'Gallery Description': () => <GalleryGrid />,
      }}
      fallback={<DefaultContent />}
    />
  );
}
